/**
 * OnboardingPage
 * ──────────────
 * Mobile-native first-launch experience shown INSIDE the app
 * (Despia wrapper or installed PWA). Replaces the web LandingPage
 * as the entry surface for app users so they don't feel like they
 * downloaded a WebView of a marketing site.
 *
 * UX design:
 *   • Full-screen, safe-area padded (iOS notch / Android gesture bar)
 *   • 3 swipeable slides (touch drag + dot indicators + buttons)
 *   • "Skip" in the top-right for returning-curious users
 *   • Final slide has both CTAs: "Continue as guest" + "Sign in"
 *   • Answers the user's constraint: someone who downloads from the
 *     stores can still see (a) what it is, (b) that it's free
 *
 * Flow:
 *   /  (in-app + first launch)  ──►  /onboarding  ──►  /login | /app
 *   /  (in-app + returning)     ──►  /login       (skips this page)
 *   /  (web visitor)            ──►  LandingPage  (unchanged)
 *
 * Sets `eili_app_onboarding_seen_v1` in localStorage on completion
 * so the carousel never replays for the same install.
 */

import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessageSquare, BookOpen, Sparkles, ShieldCheck, ArrowRight, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { markOnboardingSeen } from '@/utils/runtimeContext';
import { API_URL as API } from '@/utils/apiConfig';
import axios from 'axios';

// ─── Slide content ───────────────────────────────────────────────
// Kept as data (not JSX) so slides render inside one map and swipe
// state stays trivially indexable. Icons pulled at render time.

const SLIDES = [
  {
    key: 'welcome',
    useLogo: true, // Welcome slide shows the brand logo, not a feature icon
    eyebrow: 'Welcome to Eili',
    title: "There's more in the Word than you've ever been shown.",
    body: 'A Bible study companion that opens the passages you skip, unpacks the original languages, and walks you through what Scripture actually says.',
  },
  {
    key: 'chat',
    icon: MessageSquare,
    eyebrow: 'Every question welcome',
    title: 'Ask anything — verse by verse.',
    body: "From \"who wrote this?\" to the passages you've been quietly wondering about, answered in real time with cross-references and historical context.",
  },
  {
    key: 'depth',
    icon: BookOpen,
    eyebrow: 'Scholar-grade depth',
    title: 'Context most reading plans skip.',
    body: 'Greek and Hebrew roots, cross-references, and historical background — the kind of depth you used to need a $400+ desktop tool for.',
  },
  {
    key: 'free',
    icon: ShieldCheck,
    eyebrow: 'Free forever for daily study',
    title: 'No credit card required.',
    body: 'Read the Bible, journal reflections, and chat with Eili — free, forever. Premium tools available when you want them. Cancel anytime.',
    isFinal: true,
  },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);
  const [guestLoading, setGuestLoading] = useState(false);
  const [dragDelta, setDragDelta] = useState(0);
  const touchStartX = useRef(null);

  const total = SLIDES.length;
  const slide = SLIDES[index];
  const Icon = slide.icon;

  // ─── Navigation actions ──────────────────────────────────────
  const finish = useCallback(() => {
    markOnboardingSeen();
  }, []);

  const goNext = useCallback(() => {
    if (index < total - 1) {
      setIndex(i => i + 1);
    }
  }, [index, total]);

  const goPrev = useCallback(() => {
    if (index > 0) setIndex(i => i - 1);
  }, [index]);

  const skip = useCallback(() => {
    finish();
    navigate('/login', { replace: true });
  }, [finish, navigate]);

  const signIn = useCallback(() => {
    finish();
    navigate('/login', { replace: true });
  }, [finish, navigate]);

  // Guest start — mirrors LandingPage.handleGuestStart. Creates an
  // anonymous session server-side so the user lands directly in /app
  // with a working session cookie. Falls back to /signup if the
  // endpoint isn't reachable so we never dead-end the user.
  const startAsGuest = useCallback(async () => {
    if (guestLoading) return;
    setGuestLoading(true);
    try {
      await axios.post(`${API}/auth/guest`, {}, { withCredentials: true });
      finish();
      navigate('/app', { replace: true });
    } catch {
      finish();
      navigate('/signup', { replace: true });
    } finally {
      setGuestLoading(false);
    }
  }, [guestLoading, finish, navigate]);

  // ─── Touch swipe handlers ────────────────────────────────────
  // Simple horizontal-drag detection: threshold 60px = commit swipe.
  // We track drag delta live so the slide follows the finger for
  // native-feeling responsiveness, then spring back on release.

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setDragDelta(0);
  };
  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    // Only allow drag in the valid direction (no drag past ends).
    if ((index === 0 && dx > 0) || (index === total - 1 && dx < 0)) {
      setDragDelta(dx * 0.25); // rubber-band resistance at edges
    } else {
      setDragDelta(dx);
    }
  };
  const onTouchEnd = () => {
    const dx = dragDelta;
    setDragDelta(0);
    touchStartX.current = null;
    if (Math.abs(dx) < 60) return;
    if (dx < 0) goNext(); else goPrev();
  };

  // Note: this page is publicly reachable via /onboarding. In-app
  // visitors are redirected here from HomeRouteSwitch; web visitors
  // who navigate here directly still see the flow — no harm done.

  return (
    <div
      className="fixed inset-0 flex flex-col overflow-hidden"
      style={{
        background: 'var(--theme-bg, #0f172a)',
        color: 'var(--theme-text, #f1f5f9)',
        // Despia native injects `--safe-area-top` / `--safe-area-bottom`
        // as CSS variables at runtime (its WebView doesn't provide the
        // standard `env(safe-area-inset-*)` values). We use the unified
        // pattern — `var(--safe-area-top, env(...))` — so this page
        // respects the iOS notch / dynamic island on:
        //   • Despia native iOS  (reads --safe-area-top)
        //   • iOS Safari PWA     (reads env(safe-area-inset-top))
        //   • Android Despia     (reads --safe-area-top)
        //   • Desktop / web      (falls back to 0px)
        // The extra 8px baseline on top keeps the logo + Skip visually
        // clear of the status bar even on devices with a very thin
        // status area (older iPhones, Android).
        paddingTop: 'calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px)',
        paddingBottom: 'var(--safe-area-bottom, env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'var(--sal, env(safe-area-inset-left, 0px))',
        paddingRight: 'var(--sar, env(safe-area-inset-right, 0px))',
      }}
      data-testid="onboarding-page"
    >
      {/* Top bar — logo on the left, progress dots centered, skip on the right */}
      <div className="flex items-center justify-between px-5 pt-3 pb-2 shrink-0">
        <img
          src="/logo.png"
          alt={t('generated.onboardingpage_eili_43b2')}
          className="h-7 w-7"
          data-testid="onboarding-logo"
        />
        <div className="flex items-center gap-1.5" aria-label={t('generated.onboardingpage_progress_193a')} data-testid="onboarding-dots">
          {SLIDES.map((s, i) => (
            <span
              key={s.key}
              className="block rounded-full transition-all"
              style={{
                width: i === index ? 20 : 6,
                height: 6,
                background: i === index ? 'var(--theme-accent, #6366f1)' : 'var(--glass-border, rgba(148,163,184,0.35))',
              }}
            />
          ))}
        </div>
        <button
          onClick={skip}
          className="text-sm font-medium px-2 py-1"
          style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}
          data-testid="onboarding-skip"
        >
          {t('generated.onboardingpage_skip_26e0')}
        </button>
      </div>
      {/* Slide viewport */}
      <div
        className="flex-1 overflow-hidden"
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div
          className="h-full flex transition-transform duration-300 ease-out"
          style={{
            transform: `translateX(calc(${-index * 100}% + ${dragDelta}px))`,
            transitionDuration: dragDelta !== 0 ? '0ms' : '300ms',
          }}
        >
          {SLIDES.map((s) => {
            const SlideIcon = s.icon;
            return (
              <div
                key={s.key}
                className="min-w-full h-full flex flex-col items-center justify-center px-8 text-center"
                data-testid={`onboarding-slide-${s.key}`}
              >
                {/* Icon / logo — the welcome slide shows the actual brand
                    logo (no gradient background) so users see the app's
                    real identity first; subsequent slides use a gradient
                    circle around a feature icon. */}
                {s.useLogo ? (
                  <img
                    src="/logo.png"
                    alt={t('generated.onboardingpage_eili_43b2')}
                    className="mb-8 shrink-0"
                    style={{
                      width: 96,
                      height: 96,
                      filter: 'drop-shadow(0 20px 40px rgba(99, 102, 241, 0.35))',
                    }}
                    data-testid="onboarding-slide-logo"
                  />
                ) : (
                  <div
                    className="flex items-center justify-center rounded-full mb-8"
                    style={{
                      width: 88,
                      height: 88,
                      background: 'linear-gradient(135deg, var(--theme-accent, #6366f1) 0%, var(--theme-accentLight, #818cf8) 100%)',
                      boxShadow: '0 20px 60px -12px rgba(99, 102, 241, 0.4)',
                    }}
                  >
                    <SlideIcon className="h-11 w-11 text-white" strokeWidth={1.75} />
                  </div>
                )}
                <p
                  className="text-xs font-semibold uppercase tracking-widest mb-3"
                  style={{ color: 'var(--theme-accent, #6366f1)' }}
                >
                  {s.eyebrow}
                </p>
                <h1
                  className="text-2xl sm:text-3xl font-bold leading-tight mb-4 max-w-md"
                  style={{ color: 'var(--theme-text)' }}
                >
                  {s.title}
                </h1>
                <p
                  className="text-base leading-relaxed max-w-md"
                  style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}
                >
                  {s.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
      {/* Footer CTAs — bottom sheet feel */}
      <div className="shrink-0 px-6 pb-6 pt-3">
        {slide.isFinal ? (
          <div className="flex flex-col gap-3">
            <button
              onClick={startAsGuest}
              disabled={guestLoading}
              className="w-full py-4 rounded-2xl text-base font-semibold text-white flex items-center justify-center gap-2 shadow-lg active:opacity-90 transition-opacity disabled:opacity-70"
              style={{ background: 'linear-gradient(135deg, var(--theme-accent, #6366f1) 0%, var(--theme-accentLight, #818cf8) 100%)' }}
              data-testid="onboarding-cta-guest"
            >
              {guestLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
              {t('generated.onboardingpage_start_free_no_293f')}
            </button>
            <button
              onClick={signIn}
              className="w-full py-3.5 rounded-2xl text-base font-medium flex items-center justify-center gap-2"
              style={{
                color: 'var(--theme-text)',
                background: 'transparent',
                border: '1px solid var(--glass-border, rgba(148,163,184,0.35))',
              }}
              data-testid="onboarding-cta-signin"
            >
              {t('login.signin', 'Sign In')}
              <ArrowRight className="h-4 w-4" />
            </button>
            <p
              className="text-[11px] text-center pt-1 flex items-center justify-center gap-1.5"
              style={{ color: 'var(--theme-textSecondary, #94a3b8)' }}
              data-testid="onboarding-trust"
            >
              <ShieldCheck className="h-3.5 w-3.5" style={{ color: '#10b981' }} />
              <span>{t('generated.onboardingpage_free_forever_for_7c70')}</span>
            </p>
          </div>
        ) : (
          // 3-slot layout keeps Next centered whether Back is visible
          // or not. Left / right slots are equal fixed widths so the
          // Next button's centreline sits exactly on the viewport
          // centreline on every slide.
          (<div className="flex items-center justify-center gap-3">
            <div className="w-11 shrink-0 flex justify-start">
              {index > 0 && (
                <button
                  onClick={goPrev}
                  className="flex items-center justify-center rounded-full active:opacity-70 transition-opacity"
                  style={{
                    width: 44,
                    height: 44,
                    background: 'var(--glass-bg, rgba(148,163,184,0.08))',
                    border: '1px solid var(--glass-border, rgba(148,163,184,0.25))',
                    color: 'var(--theme-textSecondary, #94a3b8)',
                  }}
                  aria-label={t('generated.onboardingpage_back_0a9a')}
                  data-testid="onboarding-back"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
              )}
            </div>
            <button
              onClick={goNext}
              className="flex-1 max-w-xs py-4 rounded-2xl text-base font-semibold text-white flex items-center justify-center gap-2 shadow-lg active:opacity-90 transition-opacity"
              style={{ background: 'linear-gradient(135deg, var(--theme-accent, #6366f1) 0%, var(--theme-accentLight, #818cf8) 100%)' }}
              data-testid="onboarding-next"
            >
              {t('generated.onboardingpage_next_b61a')}
              <ChevronRight className="h-5 w-5" />
            </button>
            {/* Right spacer — mirrors the left Back slot so the Next
                button stays visually centered on every slide. */}
            <div className="w-11 shrink-0" aria-hidden="true" />
          </div>)
        )}
      </div>
    </div>
  );
}
